import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Eye, Sparkles, Loader2, CheckCircle2, XCircle, Send,
  Image as ImageIcon, FileText, Cpu, Wand2, AlertCircle, Clock,
  LayoutGrid, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Generate a futuristic city skyline at dusk with neon signage",
  "Create a product hero image for a luxury watch on marble",
  "Plan a 3-step social media campaign for a coffee brand",
  "Design a moodboard for a cyberpunk short film",
  "Generate a portrait of a confident CEO for LinkedIn",
  "Create a billboard ad for a new sneaker drop",
];

export default function OraclePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const headers = { Authorization: `Bearer ${token}` };

  const [capabilities, setCapabilities] = useState([]);
  const [instruction, setInstruction] = useState('');
  const [starterHint, setStarterHint] = useState(null); // Track Delphi-suggested starter
  const [running, setRunning] = useState(false);
  const [task, setTask] = useState(null); // current task object
  const [history, setHistory] = useState([]); // past tasks
  const [boards, setBoards] = useState([]);
  const [pushToCanvas, setPushToCanvas] = useState(false);
  const [canvasTarget, setCanvasTarget] = useState('new'); // 'new' or board id
  const pollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/agent/capabilities`).then(r => setCapabilities(r.data?.capabilities || [])).catch(() => {});
    if (token) {
      axios.get(`${API}/canvas/boards`, { headers }).then(r => setBoards(r.data || [])).catch(() => {});
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [token]);

  // Pre-fill instruction from ?starter= query param (set by onboarding redirect)
  useEffect(() => {
    const starter = searchParams.get('starter');
    if (starter && !instruction) {
      setInstruction(starter);
      setStarterHint(starter);
      // Focus the textarea so users can immediately edit or run
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(starter.length, starter.length);
        }
      }, 100);
      // Clear the query param so refresh doesn't re-prefill on top of user edits
      const next = new URLSearchParams(searchParams);
      next.delete('starter');
      setSearchParams(next, { replace: true });
    }
  }, []);

  const poll = (taskId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 180) {
        clearInterval(pollRef.current);
        setRunning(false);
        toast.error('Task timed out');
        return;
      }
      try {
        const { data } = await axios.get(`${API}/agent/tasks/${taskId}`);
        setTask(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollRef.current);
          setRunning(false);
          setHistory(h => [{ ...data, task_id: taskId, ts: Date.now() }, ...h].slice(0, 10));
          if (data.status === 'completed') toast.success('The Oracle has finished');
          else toast.error(data.error || 'Task failed');
        }
      } catch (e) {
        // continue polling
      }
    }, 2500);
  };

  const handleRun = async (text) => {
    const instr = (text ?? instruction).trim();
    if (!instr) return toast.error('Tell The Oracle what to create');
    setRunning(true);
    setTask({ status: 'planning', instruction: instr, steps: [], current_step: 0 });
    try {
      const body = { instruction: instr };
      if (pushToCanvas) {
        body.canvas_board_id = canvasTarget;
        if (canvasTarget === 'new') {
          body.canvas_board_name = `Oracle: ${instr.slice(0, 40)}`;
        }
      }
      const { data } = await axios.post(`${API}/agent/run`, body, { headers });
      toast.info('The Oracle is planning...');
      poll(data.task_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start task');
      setRunning(false);
      setTask(null);
    }
  };

  const statusBadge = (s) => {
    if (s === 'completed') return <span className="text-[10px] uppercase tracking-wider text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Done</span>;
    if (s === 'failed') return <span className="text-[10px] uppercase tracking-wider text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" />Failed</span>;
    if (s === 'running') return <span className="text-[10px] uppercase tracking-wider text-orange-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Running</span>;
    return <span className="text-[10px] uppercase tracking-wider text-white/40 flex items-center gap-1"><Clock className="w-3 h-3" />Queued</span>;
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="oracle-page">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-orange-500 via-amber-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Eye className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="oracle-title">
                  The Oracle
                  <span className="text-[10px] uppercase tracking-wider text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-md">Beta</span>
                </h1>
                <p className="text-xs sm:text-sm text-white/40 hidden sm:block">Your supercomputer agent — orchestrates multi-step creative workflows</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 sm:gap-6">
          {/* Main column */}
          <div className="space-y-6">
            {/* Instruction box */}
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              {starterHint && instruction === starterHint && (
                <div className="mb-3 flex items-start gap-2 p-3 rounded-md bg-gradient-to-r from-orange-500/[0.08] to-amber-500/[0.04] border border-orange-500/20" data-testid="oracle-starter-hint">
                  <Sparkles className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-[11px] uppercase tracking-wider text-orange-300 font-semibold mb-0.5">Suggested for you</p>
                    <p className="text-[12px] text-white/70 leading-snug">Delphi has pre-filled a starter prophecy based on your onboarding. Tweak it and hit <kbd className="px-1 py-0.5 bg-black/40 rounded text-[10px]">⌘/Ctrl + Enter</kbd>, or replace it entirely.</p>
                  </div>
                  <button onClick={() => { setInstruction(''); setStarterHint(null); }} data-testid="oracle-starter-clear"
                    className="text-[11px] text-white/40 hover:text-white/80 transition-colors px-2 py-1 rounded">
                    Clear
                  </button>
                </div>
              )}
              <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Tell The Oracle what to do</label>
              <Textarea
                ref={textareaRef}
                value={instruction}
                onChange={e => { setInstruction(e.target.value); if (starterHint && e.target.value !== starterHint) setStarterHint(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleRun(); }}
                placeholder="e.g. Generate three product hero shots of a minimalist white sneaker, then write Instagram captions for each."
                data-testid="oracle-instruction"
                className="bg-black/40 border-white/[0.06] text-white min-h-[120px] resize-none"
              />
              <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                <p className="text-[11px] text-white/30 flex items-center gap-1.5">
                  <AlertCircle className="w-3 h-3" />
                  The Oracle plans, then executes step-by-step. Press <kbd className="px-1.5 py-0.5 bg-black/40 rounded text-[10px]">⌘/Ctrl + Enter</kbd> to run.
                </p>
                <Button onClick={() => handleRun()} disabled={running} data-testid="oracle-run-btn"
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0">
                  {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working</> : <><Send className="w-4 h-4 mr-2" />Invoke Oracle</>}
                </Button>
              </div>

              {/* Canvas integration */}
              <div className="mt-3 pt-3 border-t border-white/[0.05] flex flex-wrap items-center gap-3" data-testid="oracle-canvas-row">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={pushToCanvas} onChange={e => setPushToCanvas(e.target.checked)}
                    data-testid="oracle-canvas-toggle"
                    className="w-3.5 h-3.5 accent-orange-500" />
                  <span className="text-[12px] text-white/70 flex items-center gap-1.5">
                    <LayoutGrid className="w-3 h-3 text-orange-400" />
                    Drop results onto a Canvas board
                  </span>
                </label>
                {pushToCanvas && (
                  <select value={canvasTarget} onChange={e => setCanvasTarget(e.target.value)}
                    data-testid="oracle-canvas-target"
                    className="bg-black/40 border border-white/10 rounded-md px-2 py-1 text-[12px] text-white">
                    <option value="new">+ Create new board</option>
                    {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                )}
              </div>

              {!task && (
                <div className="mt-4 pt-4 border-t border-white/[0.05]">
                  <p className="text-[10px] uppercase tracking-wider text-white/30 mb-2">Suggestions</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="oracle-suggestions">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={i} onClick={() => { setInstruction(s); }} data-testid={`oracle-suggestion-${i}`}
                        className="text-[11px] text-white/60 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-md px-2.5 py-1.5 transition-all">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Live task */}
            {task && (
              <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5" data-testid="oracle-task">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-orange-400" />
                    <h3 className="text-sm font-semibold text-white/90">Current invocation</h3>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md ${
                    task.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
                    task.status === 'failed' ? 'bg-red-500/10 text-red-400' :
                    'bg-orange-500/10 text-orange-400'
                  }`} data-testid="oracle-status">{task.status}</span>
                </div>

                <div className="mb-4 p-3 bg-black/30 rounded-md border border-white/[0.04]">
                  <p className="text-[11px] uppercase tracking-wider text-white/30 mb-1">Instruction</p>
                  <p className="text-sm text-white/80">{task.instruction}</p>
                </div>

                {task.status === 'planning' && (
                  <div className="flex items-center gap-2 text-white/50 text-sm" data-testid="oracle-planning">
                    <Loader2 className="w-4 h-4 animate-spin text-orange-400" />The Oracle is composing a plan...
                  </div>
                )}

                {(task.steps || []).length > 0 && (
                  <div className="space-y-2" data-testid="oracle-steps">
                    {task.steps.map((step, idx) => (
                      <div key={idx} data-testid={`oracle-step-${idx}`}
                        className={`p-3 rounded-md border ${
                          step.status === 'completed' ? 'border-emerald-500/20 bg-emerald-500/[0.04]' :
                          step.status === 'failed' ? 'border-red-500/20 bg-red-500/[0.04]' :
                          step.status === 'running' ? 'border-orange-500/30 bg-orange-500/[0.06]' :
                          'border-white/[0.06] bg-black/20'
                        }`}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-white/30">Step {idx + 1}</span>
                            {statusBadge(step.status)}
                          </div>
                        </div>
                        <p className="text-[13px] text-white/80">{step.description}</p>
                        {step.result?.type === 'image' && step.result?.url && (
                          <img src={step.result.url} alt="" className="mt-2 rounded-md max-h-64 w-auto" data-testid={`oracle-step-image-${idx}`} />
                        )}
                        {step.result?.type === 'text' && step.result?.content && (
                          <div className="mt-2 p-2 bg-black/40 rounded text-[12px] text-white/70 whitespace-pre-wrap" data-testid={`oracle-step-text-${idx}`}>{step.result.content}</div>
                        )}
                        {step.result?.type === 'info' && (
                          <p className="mt-1 text-[11px] text-white/50 italic">{step.result.message}</p>
                        )}
                        {step.result?.error && (
                          <p className="mt-1 text-[11px] text-red-400">{step.result.error}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {task.status === 'completed' && task.result && (
                  <div className="mt-4 p-3 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-md flex flex-wrap items-center justify-between gap-3" data-testid="oracle-result">
                    <p className="text-xs text-emerald-400 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Completed {task.result.steps_completed} / {task.result.steps_total} steps
                    </p>
                    {task.canvas_board_id && (
                      <Button size="sm" onClick={() => navigate(`/canvas?board=${task.canvas_board_id}`)}
                        data-testid="oracle-view-canvas-btn"
                        className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 border border-orange-500/30">
                        <LayoutGrid className="w-3.5 h-3.5 mr-1.5" />
                        View on Canvas
                        <ExternalLink className="w-3 h-3 ml-1.5" />
                      </Button>
                    )}
                  </div>
                )}

                {task.error && (
                  <div className="mt-4 p-3 bg-red-500/[0.06] border border-red-500/20 rounded-md">
                    <p className="text-xs text-red-400 flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" />{task.error}</p>
                  </div>
                )}
              </section>
            )}

            {/* History */}
            {history.length > 0 && (
              <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5" data-testid="oracle-history">
                <h3 className="text-xs uppercase tracking-wider text-white/40 mb-3">Recent invocations</h3>
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.task_id} className="p-3 bg-black/30 rounded-md border border-white/[0.04] flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-white/80 truncate">{h.instruction}</p>
                        <p className="text-[10px] text-white/30 mt-0.5">{new Date(h.ts).toLocaleTimeString()}</p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                        h.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                      }`}>{h.status}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar: capabilities */}
          <aside className="space-y-4">
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-4 sticky top-4">
              <h3 className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-1.5"><Wand2 className="w-3 h-3" />Capabilities</h3>
              <div className="space-y-1.5" data-testid="oracle-capabilities">
                {capabilities.map(c => (
                  <div key={c.id} data-testid={`capability-${c.id}`}
                    className="p-2.5 rounded-md bg-black/30 border border-white/[0.04]">
                    <p className="text-[12px] font-medium text-white/90">{c.name}</p>
                    <p className="text-[10px] text-white/40 mt-0.5 leading-relaxed">{c.desc}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[10px] text-white/30 leading-relaxed">
                The Oracle uses GPT-class reasoning to plan and dispatch tasks across all of Delphi AI&apos;s creative tools.
              </p>
            </section>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}
